/**
 * PasswordInput — the app's one password field: a normal <input> plus a
 * show/hide eye. Each instance owns its own visibility state, so on forms
 * with several (change / reset password) every eye reveals only its own
 * field. All <input> props pass through (value, onChange, autoComplete…).
 */
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/cn";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /** Classes for the wrapper (e.g. outer margin); `className` styles the input. */
  wrapperClassName?: string;
  leadingIcon?: ReactNode;
};

export const PasswordInput = forwardRef<HTMLInputElement, Props>(function PasswordInput(
  { className, wrapperClassName, leadingIcon, ...inputProps },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const label = visible ? "Hide password" : "Show password";

  return (
    <div className={cn("relative", wrapperClassName)}>
      <input
        ref={ref}
        {...inputProps}
        type={visible ? "text" : "password"}
        // room for the eye; twMerge lets this win over the caller's pr-*
        className={cn(className, "pr-10", leadingIcon && "pl-10")}
      />
      {leadingIcon && (
        <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">
          {leadingIcon}
        </span>
      )}
      <button
        type="button" // never submits the form
        onClick={() => setVisible((v) => !v)}
        aria-label={label}
        aria-pressed={visible}
        title={label}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-muted transition-colors hover:text-ink-2 focus-visible:text-ink-2 focus-visible:outline-none"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
